import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { sanitizeRedirect } from "@/lib/safeRedirect";


type ErrorKind = "rate_limited" | "invalid_email" | "network" | "server" | "generic";

type ResetError = {
  kind: ErrorKind;
  /** Headline shown in the error panel. */
  title: string;
  /** Explanation rendered under the headline. */
  message: string;
  /** Whether retrying immediately is likely to help (controls CTA copy). */
  retryable: boolean;
  /** Seconds the retry button is disabled — used for rate-limit cooldowns. */
  cooldownSeconds?: number;
};

function classifyResetError(err: { message?: string; status?: number } | null | undefined): ResetError {
  const message = (err?.message || "").toLowerCase();
  const status = err?.status ?? 0;

  if (status === 429 || message.includes("rate") || message.includes("too many") || message.includes("429")) {
    return {
      kind: "rate_limited",
      title: "Slow down a moment",
      message: "Too many reset attempts. Wait a minute, then try again.",
      retryable: true,
      cooldownSeconds: 60,
    };
  }
  if ((message.includes("invalid") && message.includes("email")) || status === 400 || status === 422) {
    return {
      kind: "invalid_email",
      title: "Check the email address",
      message: "That doesn't look like a valid email address. Double-check it and try again.",
      retryable: true,
    };
  }
  if (status >= 500) {
    return {
      kind: "server",
      title: "Our reset service is misbehaving",
      message: "Something went wrong on our end. Give it a moment and try again.",
      retryable: true,
    };
  }
  if (message.includes("network") || message.includes("fetch") || message.includes("timeout")) {
    return {
      kind: "network",
      title: "Network hiccup",
      message: "We couldn't reach our servers. Check your connection and try again.",
      retryable: true,
    };
  }
  return {
    kind: "generic",
    title: "Couldn't send the reset email",
    message: "Something didn't go through. Please try again in a moment.",
    retryable: true,
  };
}

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const safeRedirect = sanitizeRedirect(searchParams.get("redirect"));
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<ResetError | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const sendResetEmail = async (rawEmail: string) => {
    const trimmed = rawEmail.trim();
    if (!trimmed) {
      setError({
        kind: "invalid_email",
        title: "Email is required",
        message: "Enter the email tied to your account.",
        retryable: true,
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError({
        kind: "invalid_email",
        title: "Check the email address",
        message: "That doesn't look like a valid email address.",
        retryable: true,
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const resetUrl = new URL("/reset-password", window.location.origin);
      if (safeRedirect) resetUrl.searchParams.set("redirect", safeRedirect);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: resetUrl.toString(),
      });
      if (resetError) throw resetError;
      trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_REQUEST);
      setSent(true);
      setAttempts(0);
      toast.success("Reset link sent. Check your inbox.");
    } catch (err: unknown) {
      const classified = classifyResetError(err as { message?: string; status?: number });
      setError(classified);
      setAttempts((n) => n + 1);
      if (classified.cooldownSeconds) startCooldown(classified.cooldownSeconds);
      toast.error(classified.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendResetEmail(email);
  };

  const handleRetry = () => {
    void sendResetEmail(email);
  };

  const handleResend = async () => {
    setSent(false);
    setError(null);
    setAttempts(0);
  };

  const retryDisabled = loading || cooldown > 0;
  const retryLabel = cooldown > 0
    ? `Try again in ${cooldown}s`
    : loading
    ? "Sending…"
    : "Try again";

  return (
    <>
      <Helmet>
        <title>Forgot password — Trends Discovery</title>
        <meta name="description" content="Reset your Trends Discovery password. Enter the email tied to your lifetime license and we'll send a secure reset link." />
        <link rel="canonical" href="https://trendsdiscovery.com/forgot-password" />
        <meta property="og:title" content="Forgot password — Trends Discovery" />
        <meta property="og:url" content="https://trendsdiscovery.com/forgot-password" />
      </Helmet>
    <div className="min-h-screen grid place-items-center px-4 bg-hero-radial">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-elevated">
        <Link to="/auth" className="font-mono text-xs text-primary">← back to sign in</Link>
        <h1 className="font-display text-3xl font-bold mt-4">Reset password</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Enter the email tied to your license — we'll send a secure reset link.
        </p>

        {sent ? (
          <div className="mt-8 space-y-4">
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 text-sm">
              <div className="font-mono text-xs text-primary mb-2">// LINK DISPATCHED</div>
              If an account exists for <span className="font-mono break-all">{email}</span>, a reset link is on
              its way. The link expires in <strong>1 hour</strong>.
              <div className="mt-3 text-xs text-muted-foreground">
                Didn't get it? Check your spam folder, or wait a minute before requesting another.
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={handleResend}>
              Send to a different email
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                autoComplete="email"
                aria-invalid={!!error}
                aria-describedby={error ? "reset-error" : undefined}
                disabled={loading}
              />
            </div>

            {error && (
              <div
                id="reset-error"
                role="alert"
                aria-live="polite"
                data-testid="reset-error-panel"
                data-error-kind={error.kind}
                className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm space-y-3"
              >
                <div>
                  <div className="font-mono text-xs text-destructive mb-1">// COULDN'T SEND</div>
                  <div className="font-semibold text-foreground">{error.title}</div>
                  <div className="text-muted-foreground mt-1">{error.message}</div>
                </div>
                {error.retryable && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleRetry}
                    disabled={retryDisabled}
                    data-testid="reset-retry-button"
                  >
                    {retryLabel}
                  </Button>
                )}
                {attempts >= 2 && error.kind !== "rate_limited" && (
                  <p className="text-xs text-muted-foreground">
                    Still stuck after a few tries?{" "}
                    <a href="mailto:support@trendsdiscovery.com" className="text-primary hover:underline">
                      Email support
                    </a>{" "}
                    and we'll sort it out.
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full"
              disabled={loading || cooldown > 0}
            >
              {loading
                ? "Sending…"
                : cooldown > 0
                ? `Wait ${cooldown}s`
                : "Send reset link"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Remembered it?{" "}
              <Link to="/auth" className="text-primary hover:underline">
                Sign in instead
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
    </>
  );
}
